<script>
import axios from 'axios';
import qs from 'qs';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faSyncAlt,
} from '@fortawesome/pro-solid-svg-icons';
import DmBatches from '@/components/DmBatches.vue';
import DmTransactions from '@/components/DmTransactions.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import DateUtils from '@/mixins/DateUtils';

library.add(
  faAngleDoubleRight,
  faSyncAlt,
);

export default {
  mixins: [
    DateUtils,
  ],
  components: {
    DmBatches,
    DmTransactions,
    EsHeader,
    EsException,
    EsLoader,
  },
  watch: {
    type() {
      this.refresh();
    },
  },
  data() {
    return {
      batchesCompleted: [],
      batchesPending: [],
      errorBatchCompleted: null,
      errorBatchPending: null,
      loadingCompleted: null,
      loadingPending: null,
      transactionCompleted: [],
      transactionPending: [],
      type: null,
      pendingStatuses: [],
    };
  },
  created() {
    this.type = 'batches';
    this.pendingStatuses = [
      { description: 'RUNNING' },
      { description: 'COMPLETED_WITH_PENDING', inTransactions: true },
      { description: 'QUEUED' },
      { description: 'LOADED' },
    ];
  },
  computed: {
    batchesPendingStatuses() {
      return this.pendingStatuses.map(status => status.description);
    },
    matchHistoryRoute() {
      return `matchHistory/${this.type === 'batches' ? 'batches' : 'transactions'}`;
    },
    transactionsPendingStatuses() {
      return this.pendingStatuses
      .filter(status => status.inTransactions)
      .map(status => status.description );
    },
  },
  methods: {
    buildQuery(days, status) {
      const query = {};

      if (status === 'pending') {
        query.status = this.type === 'batches' ?
          this.batchesPendingStatuses :
          this.transactionsPendingStatuses;
      } else if (status === 'completed') {
        query.status = ['COMPLETED', 'ERRORED'];
      }
      query.startDate = this.buildDateString(days);

      return query;
    },
    refresh() {
      if (this.type === 'batches') {
        this.transactionPending = [];
        this.transactionCompleted = [];
        this.getBatches();
      } else if (this.type === 'transactions') {
        this.batchesPending = [];
        this.batchesCompleted = [];
        this.getTransactions();
      }
    },
    async completedBatches() {
      try {
        this.loadingCompleted = true;
        this.errorBatchCompleted = false;

        const sort = [{
          field: 'id',
          order: 'DESC',
        }];

        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'false');
        params.append('dashboard', 'true');
        params.append('sort', JSON.stringify(sort));

        const res = await axios.get('directmatch/batches', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        });
        this.batchesCompleted = res.data.data;
      } catch (e) {
        this.errorBatchCompleted = true;
      } finally {
        this.loadingCompleted = false;
      }
    },
    async pendingBatches() {
      try {
        this.loadingPending = true;
        this.errorBatchPending = false;

        const sort = [{
          field: 'id',
          order: 'DESC',
        }];

        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'true');
        params.append('dashboard', 'true');
        params.append('sort', JSON.stringify(sort));

        const res = await axios.get('directmatch/batches', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        });
        this.batchesPending = res.data.data;
      } catch (e) {
        this.errorBatchPending = true;
      } finally {
        this.loadingPending = false;
      }
    },
    async pendingTransactions() {
      try {
        this.loadingPending = true;
        this.errorBatchPending = false;

        const sort = [{
          field: 'id',
          order: 'DESC',
        }];

        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'true');
        params.append('sort', JSON.stringify(sort));

        const res = await axios.get('directmatch/transactions/latest', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        });
        this.transactionPending = res.data;
      } catch (e) {
        this.errorBatchPending = true;
      } finally {
        this.loadingPending = false;
      }
    },
    async completedTransactions() {
      try {
        this.loadingCompleted = true;
        this.errorBatchCompleted = false;

        const sort = [{
          field: 'id',
          order: 'DESC',
        }];

        const params = new URLSearchParams();
        params.append('pageSize', '5');
        params.append('open', 'false');
        params.append('sort', JSON.stringify(sort));

        const res = await axios.get('directmatch/transactions/latest', {
          params,
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        });
        this.transactionCompleted = res.data;
      } catch (e) {
        this.errorBatchCompleted = true;
      } finally {
        this.loadingCompleted = false;
      }
    },
    async getBatches() {
      await Promise.all([
        this.completedBatches(),
        this.pendingBatches(),
      ]);
    },
    async getTransactions() {
      await Promise.all([
        this.completedTransactions(),
        this.pendingTransactions(),
      ]);
    },
    updateType(type) {
      this.type = type;
    },
  },
};
</script>

<template lang="html">
  <div class="document">
    <div class="header">
      <es-header>
        <template #header>
          <div>
            <div>Recent Activity</div>
          </div>
        </template>
        <template #actions>
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <a @click="refresh"
                  title = "Refresh">
                    <font-awesome-icon :icon="['fas', 'sync-alt']" size="lg"/>
                  </a>
                </div>
              </li>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <router-link
                    :to="{ path: matchHistoryRoute }"
                    title = "Go to Match History">
                    <font-awesome-icon :icon="['far', 'angle-double-right']" size="2x"/>
                  </router-link>
                </div>
              </li>
            </ul>
        </template>
      </es-header>
    </div>
    <div class="tabs-container">
      <div class="tabs">
        <ul>
          <li :class="{ 'is-active': this.type === 'batches' }">
            <a @click="updateType('batches')">
              BATCHES
            </a>
          </li>
          <li :class="{ 'is-active': this.type === 'transactions' }">
            <a @click="updateType('transactions')">
              TRANSACTIONS
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div class="content">
      <div class="is-flex" style="margin-top: 1rem; margin-bottom: 0.5rem">
        <!-- Pending -->
        <div class="has-text-weight-bold has-text-left">
          Pending - 5 Most Recent
        </div>
        <div class="is-flex drillin-controls has-text-primary has-text-weight-bold">
          <a class="padding-left--small padding-right--small border-right" style="font-size:12px">
            <router-link
              :to="{
                path: matchHistoryRoute,
                query: buildQuery(6, 'pending'),
              }">
              View Past 7 Days
            </router-link>
          </a>
          <a class="padding-left--small" style="font-size:12px">
            <router-link
              :to="{
                path: matchHistoryRoute,
                query: buildQuery(29, 'pending'),
              }">
              View All <font-awesome-icon :icon="['far', 'angle-double-right']" style="font-size: 12px;"/>
            </router-link>
          </a>
        </div>
      </div>
      <es-loader v-if="loadingPending" style="margin: 3rem"/>
      <es-exception v-else-if="!loadingPending && errorBatchPending" margin="3rem"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-exception v-else-if="!loadingPending && !errorBatchPending && !batchesPending.length && !transactionPending.length" margin="3rem"
        exceptionMessage="No results found."
      />
      <div v-else-if="!loadingPending && !errorBatchPending">
        <dm-transactions v-if="type === 'transactions'" style="margin-bottom: 1rem"
          :transactionList="transactionPending"
        />
        <dm-batches v-else style="margin-bottom: 1rem"
          :batchList="batchesPending"
        />
      </div>

      <!-- Completed -->
      <div class="is-flex" style="margin-top: 2rem margin-bottom: 0.5rem">
        <div class="has-text-weight-bold has-text-left">
          Completed - 5 Most Recent
        </div>
        <div class="is-flex drillin-controls has-text-primary has-text-weight-bold">
          <a class="padding-left--small padding-right--small border-right" style="font-size:12px">
            <router-link
              :to="{
                path: matchHistoryRoute,
                query: buildQuery(6, 'completed'),
              }">
              View Past 7 Days
            </router-link></a>
          <a class="padding-left--small" style="font-size:12px">
            <router-link
              :to="{
                path: matchHistoryRoute,
                query: buildQuery(29, 'completed'),
              }"
            >
              View All
              <font-awesome-icon :icon="['far', 'angle-double-right']" style="font-size: 12px;"/>
            </router-link>
          </a>
        </div>
      </div>

      <es-loader v-if="loadingCompleted" style="margin: 3rem"/>
      <es-exception v-else-if="!loadingCompleted && errorBatchCompleted" margin="3rem"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <es-exception v-else-if="!loadingCompleted && !errorBatchCompleted && !batchesCompleted.length && !transactionCompleted.length" margin="3rem"
        exceptionMessage="No results found."
      />
      <div v-else-if="!loadingCompleted && !errorBatchCompleted">
        <dm-transactions v-if="type === 'transactions'" style="margin-bottom: 1rem"
          :transactionList="transactionCompleted"
        />
        <dm-batches v-else style="margin-bottom: 1rem"
          :batchList="batchesCompleted"
        />
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
  .drillin-controls
    margin-left: auto
  .padding-left--small
    padding-left: 0.5rem
  .padding-right--small
    padding-right: 0.5rem
  .border-right
    border-right: 1px solid #ccc
</style>
